import { useState } from 'react';
import { describeError } from '@pc/core';
import {
  Button,
  Field,
  Input,
  READ_ONLY_TOOLTIP,
  useCanWriteOrg,
} from '@pc/ui';
import type {
  FulfillmentOrderFulfillmentShipment,
  ShippingShipmentAddress,
} from '@pc/db';
import { ShippingLabelPanel } from '@pc/modules-shipping';
import { useActiveCtx } from '../useCtx';
import { useRecordManualShipmentTracking } from '../queries';
import { useModuleEnabled } from '../useModuleEnabled';

const MANUAL_CARRIERS = [
  { value: 'ups', label: 'UPS' },
  { value: 'usps', label: 'USPS' },
  { value: 'fedex', label: 'FedEx' },
  { value: 'dhl', label: 'DHL' },
  { value: 'other', label: 'Other' },
] as const;

interface Props {
  shipment: FulfillmentOrderFulfillmentShipment;
  orderId: string;
  shipTo: ShippingShipmentAddress;
}

/**
 * Per-shipment label path on the order detail page. Primary: buy a Shippo
 * label and attach it to this shipment. Secondary: record 3rd-party tracking
 * when the label was purchased outside the platform.
 */
export function ShipmentFulfillmentSection({ shipment, orderId, shipTo }: Props) {
  const shippingEnabled = useModuleEnabled('shipping');
  const cancelled = Boolean(shipment.cancelled_at);
  const hasTracking = Boolean(shipment.tracking_number?.trim());
  const canLabel = !cancelled && !shipment.shipped_at && !hasTracking;

  if (!canLabel) return null;

  return (
    <div className="mt-3 border-t border-rule pt-3 flex flex-col gap-3">
      {shippingEnabled === true ? (
        <ShippingLabelPanel
          orderId={orderId}
          shipTo={shipTo}
          shipmentId={shipment.id}
          variant="shipment"
        />
      ) : shippingEnabled === false ? (
        <p className="text-xs text-muted">
          Add the Shipping module to buy Shippo labels here, or record a
          3rd-party tracking number below.
        </p>
      ) : null}
      <ManualTrackingToggle
        orderId={orderId}
        shipmentId={shipment.id}
        showShippoHint={shippingEnabled === true}
      />
    </div>
  );
}

function ManualTrackingToggle({
  shipmentId,
  showShippoHint,
}: {
  orderId: string;
  shipmentId: string;
  showShippoHint: boolean;
}) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        className="text-xs text-muted hover:text-ink underline"
        onClick={() => setOpen(true)}
      >
        {showShippoHint
          ? 'Or record 3rd-party tracking...'
          : 'Record 3rd-party tracking...'}
      </button>
    );
  }

  return (
    <ManualTrackingForm
      shipmentId={shipmentId}
      onCancel={() => setOpen(false)}
      onSuccess={() => setOpen(false)}
    />
  );
}

function ManualTrackingForm({
  shipmentId,
  onCancel,
  onSuccess,
}: {
  shipmentId: string;
  onCancel: () => void;
  onSuccess: () => void;
}) {
  const ctx = useActiveCtx();
  const canWrite = useCanWriteOrg();
  const record = useRecordManualShipmentTracking(ctx);
  const [carrier, setCarrier] = useState<string>('ups');
  const [customCarrier, setCustomCarrier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [markShipped, setMarkShipped] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const resolvedCarrier =
    carrier === 'other' ? customCarrier.trim().toLowerCase() : carrier;

  async function onSubmit() {
    setErr(null);
    if (!resolvedCarrier) {
      setErr('Enter a carrier name.');
      return;
    }
    if (!trackingNumber.trim()) {
      setErr('Enter a tracking number.');
      return;
    }
    try {
      await record.mutateAsync({
        shipmentId,
        carrier: resolvedCarrier,
        trackingNumber: trackingNumber.trim(),
        trackingUrl: trackingUrl.trim() || null,
        markShipped,
      });
      onSuccess();
    } catch (e) {
      setErr(describeError(e));
    }
  }

  return (
    <div className="border border-rule bg-paper p-3 flex flex-col gap-3 text-sm">
      <p className="text-xs text-muted">
        Label bought on UPS.com, PirateShip, or another portal? Enter the
        carrier tracking number here.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Carrier">
          <select
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
            className="bg-paper border border-rule-strong rounded-none px-3 py-2 text-sm text-ink w-full"
          >
            {MANUAL_CARRIERS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>
        {carrier === 'other' ? (
          <Field label="Carrier name">
            <Input
              value={customCarrier}
              onChange={(e) => setCustomCarrier(e.target.value)}
              placeholder="e.g. OnTrac"
            />
          </Field>
        ) : null}
        <Field label="Tracking number">
          <Input
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            placeholder="1Z999..."
            className="tabular"
          />
        </Field>
        <Field label="Tracking URL (optional)">
          <Input
            value={trackingUrl}
            onChange={(e) => setTrackingUrl(e.target.value)}
            placeholder="https://..."
          />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-xs text-ink-2">
        <input
          type="checkbox"
          checked={markShipped}
          onChange={(e) => setMarkShipped(e.target.checked)}
        />
        Mark as shipped (notify customer)
      </label>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={!canWrite || record.isPending}
          title={canWrite ? undefined : READ_ONLY_TOOLTIP}
          onClick={onSubmit}
        >
          {record.isPending ? 'Saving...' : 'Save tracking'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {err ? <p className="text-sm text-clay">{err}</p> : null}
    </div>
  );
}
